import { MonitoringUsersGroup } from '@hakimo-ui/hakimo/types';
import { useUpdateTenantMonitoringUsers } from '@hakimo-ui/hakimo/data-access';
import { toast } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { useEffect, useMemo, useState } from 'react';
import { MonitoringUsersList } from './MonitoringUsersList';

interface Props {
  tenantId: string;
  open: boolean;
  groups: MonitoringUsersGroup[] | undefined;
  isLoading: boolean;
  onBusyChange: (busy: boolean) => void;
}

export function MonitoringUsersTab({
  tenantId,
  open,
  groups,
  isLoading,
  onBusyChange,
}: Props) {
  const [pending, setPending] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return;
    setPending({});
  }, [open]);

  const updateMonitoringUsersMutation = useUpdateTenantMonitoringUsers(
    tenantId,
    () => {
      toast('Monitoring users updated successfully', { type: 'success' });
    },
    () => {
      toast('Failed to update monitoring users', { type: 'error' });
    }
  );

  const isUpdating = updateMonitoringUsersMutation.isLoading;

  useEffect(() => {
    onBusyChange(isUpdating);
  }, [isUpdating, onBusyChange]);

  const { grants, revokes } = useMemo(() => {
    const g: string[] = [];
    const r: string[] = [];
    if (!groups) return { grants: g, revokes: r };
    groups.forEach((grp) =>
      grp.users.forEach((u) => {
        if (!(u.id in pending)) return;
        const next = pending[u.id];
        if (next && !u.hasAccess) g.push(u.id);
        if (!next && u.hasAccess) r.push(u.id);
      })
    );
    return { grants: g, revokes: r };
  }, [groups, pending]);

  const handleToggleUser = (
    userId: string,
    hasAccess: boolean,
    checked: boolean
  ) => {
    setPending((prev) => {
      const next = { ...prev };
      if (checked === hasAccess) {
        delete next[userId];
      } else {
        next[userId] = checked;
      }
      return next;
    });
  };

  const handleResetUsers = () => setPending({});

  const handleApplyUsers = () => {
    updateMonitoringUsersMutation.mutate(
      { grants, revokes },
      {
        onSuccess: () => {
          setPending({});
        },
      }
    );
  };

  const pendingCount = grants.length + revokes.length;

  return (
    <div className="flex h-full flex-col">
      <MonitoringUsersList
        groups={groups}
        isLoading={isLoading}
        isUpdating={isUpdating}
        pending={pending}
        onToggleUser={handleToggleUser}
      />
      <div className="flex gap-2 border-t px-8 py-4 dark:border-gray-600">
        <Button
          variant="primary"
          onClick={handleApplyUsers}
          loading={updateMonitoringUsersMutation.isLoading}
          disabled={isUpdating || pendingCount === 0}
        >
          Apply
        </Button>
        <Button
          variant="outline"
          onClick={handleResetUsers}
          disabled={isUpdating || pendingCount === 0}
        >
          Reset
        </Button>
        <span className="text-onlight-text-2 dark:text-ondark-text-2 ml-auto self-center text-sm">
          {pendingCount === 0
            ? 'No pending changes'
            : `${grants.length} grant${grants.length === 1 ? '' : 's'}, ${
                revokes.length
              } revoke${revokes.length === 1 ? '' : 's'} pending`}
        </span>
      </div>
    </div>
  );
}

export default MonitoringUsersTab;
